import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Store, Users, Calendar, Settings, LogOut, Menu, X,
  DollarSign, TrendingUp, Check, Star, Eye, Ban, Award
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [salons, setSalons] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
    fetchSalons();
    fetchUsers();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/admin/dashboard`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalons = async () => {
    try {
      const response = await fetch(`${API}/admin/salons`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setSalons(data);
      }
    } catch (error) {
      console.error('Failed to fetch salons:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const toggleSalonVerification = async (salonId, verified) => {
    try {
      const response = await fetch(`${API}/admin/salons/${salonId}/verify?verified=${verified}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success(verified ? 'Salon verified' : 'Verification removed');
        fetchSalons();
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const toggleSalonFeatured = async (salonId, featured) => {
    try {
      const response = await fetch(`${API}/admin/salons/${salonId}/feature?featured=${featured}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success(featured ? 'Salon featured' : 'Removed from featured');
        fetchSalons();
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const toggleSalonStatus = async (salonId, active) => {
    try {
      const response = await fetch(`${API}/admin/salons/${salonId}/status?active=${active}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success(active ? 'Salon activated' : 'Salon deactivated');
        fetchSalons();
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role?role=${role}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('User role updated');
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'salons', icon: Store, label: 'Salons' },
    { id: 'users', icon: Users, label: 'Users' },
    { id: 'bookings', icon: Calendar, label: 'Bookings' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-zinc-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar */}
      <aside className={`dashboard-sidebar bg-zinc-900 text-white ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-zinc-800">
          <Link to="/" className="text-2xl font-bold">Zenvy</Link>
          <p className="text-sm text-zinc-400 mt-1">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === item.id 
                  ? 'bg-white text-zinc-900' 
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-zinc-800 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-semibold text-zinc-900">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-red-100 text-red-800">Admin</Badge>
            <span className="text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
          </div>
        </header>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6" data-testid="overview-tab">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total Users</p>
                        <p className="text-4xl font-bold">{dashboardData?.total_users || 0}</p>
                      </div>
                      <Users className="w-12 h-12 text-blue-300" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Total Salons</p>
                        <p className="text-4xl font-bold">{dashboardData?.total_salons || 0}</p>
                      </div>
                      <Store className="w-12 h-12 text-purple-300" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Total Bookings</p>
                        <p className="text-4xl font-bold">{dashboardData?.total_bookings || 0}</p>
                      </div>
                      <Calendar className="w-12 h-12 text-green-300" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-600 to-amber-700 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm">Total Revenue</p>
                        <p className="text-4xl font-bold">${dashboardData?.total_revenue || 0}</p>
                      </div>
                      <DollarSign className="w-12 h-12 text-amber-300" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* More Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-zinc-500 text-sm">Active Bookings</p>
                        <p className="text-3xl font-bold text-blue-600">{dashboardData?.active_bookings || 0}</p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-zinc-500 text-sm">Completed</p>
                        <p className="text-3xl font-bold text-green-600">{dashboardData?.completed_bookings || 0}</p>
                      </div>
                      <Check className="w-10 h-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-zinc-500 text-sm">Cancelled</p>
                        <p className="text-3xl font-bold text-red-600">{dashboardData?.cancelled_bookings || 0}</p>
                      </div>
                      <Ban className="w-10 h-10 text-red-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Bookings */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="dashboard-table w-full">
                      <thead>
                        <tr className="text-zinc-500">
                          <th>Customer</th>
                          <th>Salon</th>
                          <th>Service</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData?.recent_bookings?.map(booking => (
                          <tr key={booking.booking_id} className="border-t border-zinc-100">
                            <td>{booking.customer_name}</td>
                            <td>{booking.salon_name}</td>
                            <td>{booking.service_name}</td>
                            <td>{booking.booking_date}</td>
                            <td>
                              <Badge className={`status-${booking.status}`}>{booking.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {!dashboardData?.recent_bookings?.length && (
                      <p className="text-zinc-500 text-center py-8">No recent bookings</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Salons Tab */}
          {activeTab === 'salons' && (
            <Card data-testid="salons-tab">
              <CardHeader>
                <CardTitle>Manage Salons</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="dashboard-table w-full">
                    <thead>
                      <tr className="text-zinc-500">
                        <th>Salon</th>
                        <th>Category</th>
                        <th>City</th>
                        <th>Rating</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salons.map(salon => (
                        <tr key={salon.salon_id} className="border-t border-zinc-100">
                          <td>
                            <div className="flex items-center gap-3">
                              <div>
                                <p className="font-medium">{salon.name}</p>
                                <p className="text-sm text-zinc-500">{salon.email}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge className={salon.category === 'men' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'}>
                              {salon.category}
                            </Badge>
                          </td>
                          <td>{salon.city}</td>
                          <td>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                              {salon.rating?.toFixed(1) || '0.0'}
                            </div>
                          </td>
                          <td>
                            <div className="flex gap-1">
                              {salon.is_verified && <Badge className="bg-green-100 text-green-800">Verified</Badge>}
                              {salon.is_featured && <Badge className="bg-amber-100 text-amber-800">Featured</Badge>}
                              {!salon.is_active && <Badge className="bg-red-100 text-red-800">Inactive</Badge>}
                            </div>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={salon.is_verified ? "outline" : "default"}
                                onClick={() => toggleSalonVerification(salon.salon_id, !salon.is_verified)}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant={salon.is_featured ? "outline" : "default"}
                                onClick={() => toggleSalonFeatured(salon.salon_id, !salon.is_featured)}
                              >
                                <Award className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant={salon.is_active ? "destructive" : "default"}
                                onClick={() => toggleSalonStatus(salon.salon_id, !salon.is_active)}
                              >
                                {salon.is_active ? <Ban className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {salons.length === 0 && (
                    <p className="text-zinc-500 text-center py-8">No salons registered</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <Card data-testid="users-tab">
              <CardHeader>
                <CardTitle>Manage Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="dashboard-table w-full">
                    <thead>
                      <tr className="text-zinc-500">
                        <th>User</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.user_id} className="border-t border-zinc-100">
                          <td>
                            <p className="font-medium">{u.name}</p>
                          </td>
                          <td>{u.email}</td>
                          <td>
                            <Badge className={
                              u.role === 'admin' ? 'bg-red-100 text-red-800' :
                              u.role === 'shop_owner' ? 'bg-purple-100 text-purple-800' :
                              'bg-zinc-100 text-zinc-800'
                            }>
                              {u.role}
                            </Badge>
                          </td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                          <td>
                            <Select
                              value={u.role}
                              onValueChange={(value) => updateUserRole(u.user_id, value)}
                              disabled={u.user_id === user?.user_id}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="customer">Customer</SelectItem>
                                <SelectItem value="shop_owner">Shop Owner</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <Card data-testid="bookings-tab">
              <CardHeader>
                <CardTitle>All Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="dashboard-table w-full">
                    <thead>
                      <tr className="text-zinc-500">
                        <th>Booking ID</th>
                        <th>Customer</th>
                        <th>Salon</th>
                        <th>Service</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recent_bookings?.map(booking => (
                        <tr key={booking.booking_id} className="border-t border-zinc-100">
                          <td className="font-mono text-sm">{booking.booking_id.slice(-8)}</td>
                          <td>{booking.customer_name}</td>
                          <td>{booking.salon_name}</td>
                          <td>{booking.service_name}</td>
                          <td>{booking.booking_date} {booking.time_slot}</td>
                          <td className="font-medium">${booking.total_price}</td>
                          <td>
                            <Badge className={`status-${booking.status}`}>{booking.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
